import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Wrench, Home, Car, FileText, DollarSign, MessageCircle, LogOut, Menu, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const NAV = [
  { to: '/oficina/cliente/home', label: 'Início', icon: Home },
  { to: '/oficina/cliente/veiculos', label: 'Meus Veículos', icon: Car },
  { to: '/oficina/cliente/servicos', label: 'Serviços', icon: Wrench },
  { to: '/oficina/cliente/orcamentos', label: 'Orçamentos', icon: FileText },
  { to: '/oficina/cliente/financeiro', label: 'Financeiro', icon: DollarSign },
  { to: '/oficina/cliente/contato', label: 'Falar c/ Oficina', icon: MessageCircle },
  { to: '/oficina/cliente/perfil', label: 'Meu Perfil', icon: User },
];

function NavList({ onNavigate, user, onLogout }) {
  return (
    <>
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-lg flex items-center justify-center">
          <Wrench className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-bold text-sm leading-tight">Portal Cliente</p>
          <p className="text-blue-300/60 text-xs truncate">{user.nome}</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`portal-nav-${to.split('/').pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-blue-500/15 text-blue-200 border border-blue-500/30' : 'text-slate-400 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-white/5">
        <Button data-testid="portal-logout-btn" variant="ghost" size="sm" onClick={onLogout} className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10">
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </div>
    </>
  );
}

export default function ClientePortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('cliente_portal_user') || '{}');

  const logout = () => {
    localStorage.removeItem('cliente_portal_token');
    localStorage.removeItem('cliente_portal_user');
    navigate('/oficina/cliente');
  };

  const currentLabel = NAV.find(n => location.pathname.startsWith(n.to))?.label || 'Portal';

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <aside className="hidden md:flex w-64 bg-slate-900 border-r border-blue-500/10 flex-col">
        <NavList user={user} onLogout={logout} />
      </aside>

      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button size="sm" variant="ghost" className="text-slate-300 px-2" data-testid="portal-menu-mobile-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-slate-900 border-r border-white/10 p-0 w-72 flex flex-col">
            <NavList user={user} onLogout={logout} onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-blue-400" />
          <span className="text-white text-sm font-medium truncate max-w-[180px]">{currentLabel}</span>
        </div>
        <span className="text-blue-300/60 text-xs truncate max-w-[80px]">{user.nome || ''}</span>
      </header>

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
